import type { RecentPatientType } from '@/shared/lib/hooks/useToken';
import { MedicalServices, Role } from '@/shared/lib/types/admin';
import type { LoggedUser } from '@/shared/lib/types/graphql';
import { getAccountRedirectUrl } from '@/shared/lib/util/account';
import type { MainMenuItem, MenuSectionType } from '@/shared/model/AppFrameStore';
import { MainMenuKeys } from '@/shared/model/AppFrameStore';
import { AnnouncementSharp, Collections } from '@mui/icons-material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AssessmentIcon from '@mui/icons-material/Assessment';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import BusinessIcon from '@mui/icons-material/Business';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutlined';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DashboardIcon from '@mui/icons-material/Dashboard';
import DevicesOtherIcon from '@mui/icons-material/DevicesOther';
import EventIcon from '@mui/icons-material/Event';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import UserGroup from '@mui/icons-material/Group';
import GroupsIcon from '@mui/icons-material/Groups';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import HistoryToggleOffIcon from '@mui/icons-material/HistoryToggleOff';
import ImportExportIcon from '@mui/icons-material/ImportExport';
import EncountersIcon from '@mui/icons-material/InterpreterModeOutlined';
import ListAltIcon from '@mui/icons-material/ListAlt';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import LocalPharmacyIcon from '@mui/icons-material/LocalPharmacy';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import BillingIcon from '@mui/icons-material/PaymentOutlined';
import PeopleIcon from '@mui/icons-material/People';
import PhoneIcon from '@mui/icons-material/Phone';
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import QueueIcon from '@mui/icons-material/Queue';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import ReviewsIcon from '@mui/icons-material/Reviews';
import RouteIcon from '@mui/icons-material/Route';
import SearchIcon from '@mui/icons-material/Search';
import SettingsIcon from '@mui/icons-material/Settings';
import SummarizeIcon from '@mui/icons-material/Summarize';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import ThumbsUpDownIcon from '@mui/icons-material/ThumbsUpDown';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TuneIcon from '@mui/icons-material/Tune';
import WarningIcon from '@mui/icons-material/Warning';
import type { ListItemButtonOwnProps } from '@mui/material';
import type { ReactNode } from 'react';

/**
 * The whole left navigation, as data.
 *
 * Renaming an item is a one-line edit here; reordering is moving a line.  Nothing about the
 * order or the labels lives in the rendering components any more — MainSideMenu walks whatever
 * this file describes.
 *
 * Three things to know before editing:
 *
 * 1. The item arrays below hold the labels, paths and role rules.  Rename there.
 * 2. MENU_TREE is the sidebar's shape and order.  Reorder there.
 * 3. STORE_SECTIONS is the flat view the app-grid (SecondaryMenu) reads.
 *    Its gates are not always the sidebar's — four sections have always disagreed, and each one
 *    now says so in a comment instead of the difference being split across two files.
 *
 * The breadcrumb ribbon was the other reader of STORE_SECTIONS until this branch removed it, so
 * `ribbonPosition` below is retained but currently unread.
 */

export type MenuContext = {
  role: Role | null;
  isMtmViewable: boolean;
  isRpmViewable: boolean;
  isTcmViewable: boolean;
  currentUser: LoggedUser | null;
  /** Serving on the production hostname (isProd). */
  isProdEnvironment: boolean;
  /** Running in production mode (isModeProduction).  Not the same test as isProdEnvironment. */
  isProductionMode: boolean;
};

export type Gate = (ctx: MenuContext) => boolean;

// Role predicates, named once, so every rule below reads like the sentence a PM would say.
// `isPsm` is the ADMIN role; the product calls it PSM.
export const isSuperAdmin: Gate = ctx => ctx.role === Role.SUPER;
export const isPharmacistAdmin: Gate = ctx => ctx.role === Role.PHARMACIST_ADMIN;
export const isAdtAdmin: Gate = ctx => ctx.role === Role.ADT_ADMIN;
export const isPsm: Gate = ctx => ctx.role === Role.ADMIN;
export const isProviderAdmin: Gate = ctx => ctx.role === Role.PROVIDER;
export const isPharmacist: Gate = ctx => ctx.role === Role.PHARMACIST;

/**
 * NOTE: BaseSideMenuSideNav's icon rail uses a narrower definition that leaves PSM out, so a PSM
 * gets the full admin menu but a different rail destination.  Reconcile deliberately, not by
 * accident — that is a product decision, not a refactor.
 */
export const isAnyAdmin: Gate = ctx =>
  ctx.role !== null && [Role.SUPER, Role.PHARMACIST_ADMIN, Role.ADT_ADMIN, Role.ADMIN].includes(ctx.role);

const always: Gate = () => true;
const anyOf =
  (...gates: Gate[]): Gate =>
  ctx =>
    gates.some(gate => gate(ctx));
const not =
  (gate: Gate): Gate =>
  ctx =>
    !gate(ctx);

const mtm: Gate = ctx => ctx.isMtmViewable;
const rpm: Gate = ctx => ctx.isRpmViewable;
const tcm: Gate = ctx => ctx.isTcmViewable;

export type MenuItemDef = {
  /** Stable id.  Survives renames — use it for analytics, tests and per-role ordering. */
  key: string;
  label: string | ((ctx: MenuContext) => string);
  path: string | ((ctx: MenuContext) => string);
  icon: ReactNode;
  visible?: Gate;
  /**
   * Keeps the item out of the sidebar while leaving it in the catalog, so the site map still
   * lists it and its access rules still apply.  For pages that are real but rarely wanted.
   */
  hiddenFromNav?: boolean;
  /** Position in the breadcrumb ribbon, which no longer renders.  Kept for when it returns. */
  ribbonPosition?: number | ((ctx: MenuContext) => number);
  buttonProps?: ListItemButtonOwnProps;
};

export type MenuNodeDef = {
  /** Present when this node is also one of the legacy store sections. */
  key?: MainMenuKeys;
  /** Group header.  Omit to render the items inline, with no header. */
  title?: string;
  /** Shown on nested subgroups (Reports, Users, Providers, System); top-level groups have none. */
  icon?: ReactNode;
  visible?: Gate;
  /** Keeps the whole group out of the sidebar; its items stay on the site map. */
  hiddenFromNav?: boolean;
  /**
   * Paths that would leave this group expanded on load.  Currently unread — every group starts
   * open — but kept as the record of which routes belong to which section.
   */
  openOn?: string[];
  /** Paths that cancel `openOn` — for groups whose paths overlap another section's. */
  openUnless?: string[];
  /** A node lists items or children, never both. */
  items?: MenuItemDef[];
  children?: MenuNodeDef[];
};

const PATIENTS: MenuItemDef[] = [
  {
    key: 'patients.lookup',
    label: 'Patients Lookup',
    path: '../patients/lookup',
    icon: <SearchIcon />
  },
  {
    key: 'patients.tagged',
    label: 'Starred Patients',
    path: '../patients/tagged',
    icon: <LocalOfferIcon />
  },
  {
    key: 'patients.followUp',
    label: 'Patients to Follow Up',
    path: '../patients/patients-to-followup',
    icon: <PhoneIcon />
  },
  {
    key: 'patients.mine',
    label: 'My Patients',
    path: ctx => `../patients?search=${ctx.currentUser?.id}`,
    icon: <AssignmentIndIcon />,
    // Care Management has its own patient list, so this one only earns its place without RPM.
    visible: not(rpm),
    ribbonPosition: 0
  },
  {
    key: 'patients.conversations',
    label: 'Conversations',
    path: '../patients/conversations',
    icon: <ChatBubbleOutlineIcon />,
    ribbonPosition: 5
  },
  {
    key: 'patients.medHistory',
    label: 'Med History',
    path: '../patients/med-history-requests',
    icon: <DashboardIcon />,
    ribbonPosition: 4
  }
];

const MEDICATION_MANAGEMENT: MenuItemDef[] = [
  {
    key: 'mtm.tasks',
    label: ctx => (isAnyAdmin(ctx) ? 'Tasks' : 'My Tasks'),
    path: '../tasks',
    icon: <TaskAltIcon />
  },
  {
    key: 'mtm.myDashboard',
    label: 'My Dashboard',
    path: '../reports/my-dashboard',
    icon: <DashboardIcon />
  }
];

const CARE_MANAGEMENT: MenuItemDef[] = [
  {
    key: 'rpm.dashboard',
    label: 'Care Dashboard',
    path: '../care-management/dashboard',
    icon: <DashboardIcon fontSize="small" />
  },
  {
    key: 'rpm.patients',
    label: 'Patients',
    path: '../care-management/patients',
    icon: <PeopleIcon fontSize="small" />,
    ribbonPosition: 0
  },
  {
    key: 'rpm.providerApprovals',
    label: 'Provider Approvals',
    path: '../remote-patient-monitoring/provider-approvals',
    icon: <AssignmentTurnedInIcon fontSize="small" />,
    visible: isSuperAdmin
  },
  {
    key: 'rpm.alerts',
    label: 'Alerts',
    path: '../remote-patient-monitoring/alerts',
    // The same warning triangle as the Alerts link in the patient nav bar; a bell read as notifications.
    icon: <WarningIcon />,
    ribbonPosition: ctx => (isProviderAdmin(ctx) ? -1 : 1)
  },
  {
    key: 'rpm.followUpTasks',
    label: 'My Tasks: Follow-up',
    path: '../remote-patient-monitoring/follow-up-tasks',
    icon: <TaskAltIcon fontSize="small" />,
    ribbonPosition: 2,
    buttonProps: {
      sx: {
        textTransform: 'none'
      }
    }
  },
  {
    key: 'rpm.devices',
    label: 'Devices',
    path: '../remote-patient-monitoring/devices',
    icon: <DevicesOtherIcon />,
    ribbonPosition: 6
  },
  {
    key: 'rpm.reportsCommunication',
    label: 'Reports Communication',
    path: '../remote-patient-monitoring/reports-communication',
    icon: <ImportExportIcon />,
    ribbonPosition: 3
  },
  {
    key: 'rpm.claims',
    label: 'Claims',
    path: '../remote-patient-monitoring/claims',
    icon: <MonetizationOnIcon />,
    // Pharmacist Admin reads its own pharmacy's claims (z93r0b76qr).
    visible: anyOf(isSuperAdmin, isPsm, isPharmacistAdmin)
  },
  {
    key: 'admin.resources',
    label: 'Resources',
    path: '../administration/resources',
    icon: <Collections />,
    // One entry now: admins reached this from Administration and pharmacists from Quick Links,
    // which was the same page listed twice under two keys.
    visible: anyOf(isAnyAdmin, isPharmacist, isProviderAdmin),
    ribbonPosition: 8
  }
];

const PROVIDER_WORK: MenuItemDef[] = [
  {
    key: 'rpm.patientSchedules',
    label: 'Patient Schedules',
    path: '../remote-patient-monitoring/patient-schedules',
    icon: <HistoryToggleOffIcon />,
    // Pharmacists see their own pharmacy's schedule; the backend limits them to it.
    visible: anyOf(isSuperAdmin, isProviderAdmin, isPsm, isPharmacist, isPharmacistAdmin),
    ribbonPosition: 9
  },
  {
    key: 'rpm.workqueue',
    label: 'Workqueue',
    path: '../care-management/workqueue',
    icon: <QueueIcon />,
    visible: anyOf(isSuperAdmin, isProviderAdmin, isPsm)
  },
  {
    key: 'rpm.chartReview',
    label: 'Chart Review',
    path: '../remote-patient-monitoring/review-patients',
    icon: <ReviewsIcon />,
    visible: anyOf(isSuperAdmin, isProviderAdmin, isPsm),
    ribbonPosition: 3
  },
  {
    key: 'reports.rpmProviderSignoffs',
    label: 'Sign-Offs',
    path: '../reports/rpm-provider-signoffs',
    icon: <ReviewsIcon />,
    visible: ctx => anyOf(isSuperAdmin, isProviderAdmin, isPsm)(ctx) && rpm(ctx),
    ribbonPosition: ctx => (isProviderAdmin(ctx) ? 1 : -1)
  }
];

const TRANSITIONAL_CARE_MANAGEMENT: MenuItemDef[] = [
  {
    key: 'tcm.dashboard',
    label: 'Dashboard',
    path: '../transitional-care-management/dashboard',
    icon: <DashboardIcon fontSize="small" />,
    visible: anyOf(isSuperAdmin, isPharmacistAdmin)
  },
  {
    key: 'tcm.adtQueue',
    label: 'ADT Queue',
    path: '../transitional-care-management/adt-queue-v1',
    icon: <QueueIcon />,
    visible: anyOf(isSuperAdmin, isPharmacistAdmin)
  },
  {
    key: 'tcm.tasks',
    label: 'TCM Tasks',
    path: '../transitional-care-management/tcm-tasks',
    icon: <AssignmentTurnedInIcon />,
    visible: anyOf(isSuperAdmin, isPharmacistAdmin)
  },
  {
    key: 'tcm.discharge',
    label: 'Discharge Patients',
    path: '../transitional-care-management/discharge',
    icon: <QueueIcon />,
    visible: isAdtAdmin
  },
  {
    key: 'tcm.billing',
    label: 'Billing & Claims',
    path: '../transitional-care-management/billing',
    icon: <MonetizationOnIcon />
  }
];

const REPORTS: MenuItemDef[] = [
  {
    key: 'reports.dashboard',
    label: 'Org Dashboard',
    path: '../reports/dashboard',
    icon: <DashboardIcon fontSize="small" />,
    visible: not(anyOf(isProviderAdmin, isPharmacist))
  },
  {
    key: 'reports.billing',
    label: 'Billing',
    path: '../reports/billing',
    icon: <BillingIcon fontSize="small" />,
    visible: not(anyOf(isProviderAdmin, isPharmacist))
  },
  {
    key: 'reports.encounters',
    label: 'Encounters',
    path: '../reports/encounters',
    icon: <EncountersIcon fontSize="small" />,
    visible: not(anyOf(isProviderAdmin, isPsm, isPharmacist))
  },
  {
    key: 'reports.tasks',
    label: 'Tasks',
    path: '../reports/tasks',
    icon: <TaskAltIcon />,
    visible: not(anyOf(isProviderAdmin, isPsm, isPharmacist))
  },
  {
    key: 'reports.healthPlan',
    label: 'Health Plan',
    path: '../reports/health-plan',
    icon: <HealthAndSafetyIcon fontSize="small" />,
    visible: not(anyOf(isProviderAdmin, isPharmacist))
  },
  {
    key: 'reports.performance',
    label: 'Performance',
    path: '../reports/performance',
    icon: <TrendingUpIcon />,
    visible: mtm
  },
  {
    key: 'reports.claims',
    label: 'Claims',
    path: '../reports/claims',
    icon: <MonetizationOnIcon />,
    visible: ctx => anyOf(isSuperAdmin, isPsm)(ctx) && rpm(ctx)
  },
  {
    key: 'reports.payerChartRequests',
    label: 'Payer Chart Requests',
    path: '../reports/payer-chart-requests',
    icon: <SummarizeIcon />,
    visible: anyOf(isSuperAdmin, isPsm)
  },
  {
    key: 'reports.rpmProviders',
    label: 'Providers',
    path: '../reports/rpm-providers',
    icon: <LocalHospitalIcon />,
    visible: ctx => anyOf(isSuperAdmin, isPharmacistAdmin, isPsm)(ctx) && rpm(ctx)
  },
  {
    key: 'reports.rpmCompanies',
    label: 'RPM Pharmacies',
    path: '../reports/rpm-companies',
    icon: <LocalPharmacyIcon />,
    visible: ctx => anyOf(isSuperAdmin, isPsm)(ctx) && rpm(ctx)
  },
  {
    key: 'reports.rpmChartReviews',
    label: 'Chart Reviews',
    path: '../reports/rpm-chart-reviews',
    icon: <ReviewsIcon />,
    visible: ctx => anyOf(isSuperAdmin, isProviderAdmin, isPsm)(ctx) && rpm(ctx),
    ribbonPosition: ctx => (isProviderAdmin(ctx) ? 1 : -1)
  },
  {
    key: 'reports.userTime',
    label: 'User Time',
    path: '../reports/session-time',
    icon: <AccessTimeIcon fontSize="small" />,
    visible: not(isProviderAdmin)
  },
  {
    key: 'reports.rpmMetrics',
    label: 'Metrics',
    path: '../reports/rpm-metrics',
    icon: <SummarizeIcon fontSize="small" />
  },
  {
    key: 'reports.ccmReports',
    label: 'CCM Reports',
    path: '../reports/ccm-reports',
    icon: <AssessmentIcon fontSize="small" />,
    visible: ctx => anyOf(isSuperAdmin, isPsm)(ctx) && !mtm(ctx)
  },
  {
    // AI Search feedback, with the question asked (PHI): super admins only, and the
    // backend query enforces it too.
    key: 'reports.aiSearchFeedback',
    label: 'AI Search Feedback',
    path: '../reports/ai-search-feedback',
    icon: <ThumbsUpDownIcon fontSize="small" />,
    visible: isSuperAdmin
  }
];

const USER_ADMIN: MenuItemDef[] = [
  {
    key: 'users.status',
    label: 'Status',
    path: '../administration/dashboard',
    icon: <DashboardIcon fontSize="small" />
  },
  {
    key: 'users.manage',
    label: 'Manage',
    path: '../administration/users',
    icon: <UserGroup fontSize="small" />,
    visible: not(isPharmacist)
  }
];

const TIMESHEETS_AND_INVOICES: MenuItemDef[] = [
  {
    key: 'timesheets.timesheets',
    label: ctx => (isPharmacist(ctx) ? 'My Timesheets' : 'Timesheets'),
    path: '../administration/timesheets',
    icon: <TaskAltIcon />,
    visible: mtm
  },
  {
    key: 'timesheets.invoices',
    label: ctx => (isPharmacist(ctx) ? 'My Invoices' : 'Invoices'),
    path: '../administration/invoices',
    icon: <MonetizationOnIcon />,
    visible: mtm
  }
];

const ADMINISTRATION: MenuItemDef[] = [
  {
    key: 'admin.resources',
    hiddenFromNav: true,
    label: 'Resources',
    path: '../administration/resources',
    icon: <Collections />,
    // Access anchor.  The nav shows Resources under Care Management, which is RPM-gated; without
    // this entry an MTM-only company could not reach the page from anywhere, site map included.
    visible: anyOf(isAnyAdmin, isPharmacist, isProviderAdmin)
  },
  {
    key: 'admin.organizations',
    label: 'Organizations',
    path: '../administration/organizations',
    icon: <BusinessIcon fontSize="small" />
  },
  {
    key: 'admin.patients',
    label: 'Patients',
    path: '../administration/patients',
    icon: <GroupsIcon />,
    visible: isSuperAdmin
  },
  {
    key: 'admin.healthPlans',
    label: 'Health Plans',
    path: '../administration/health-plans',
    icon: <HealthAndSafetyIcon />
  },
  {
    key: 'admin.insurancePlans',
    label: 'Insurance Plans',
    path: '../administration/insurance-plans',
    icon: <HealthAndSafetyIcon />,
    // Merges move patients across every company, so the page itself keeps everyone but super
    // admins and PSMs out — match that here so the item is not a dead end.
    visible: anyOf(isSuperAdmin, isPsm)
  },
  {
    key: 'admin.myPerformance',
    label: 'My Performance',
    path: '../administration/my-performance',
    icon: <QueryStatsIcon />,
    visible: isPharmacistAdmin
  },
  {
    key: 'admin.logs',
    label: 'Logs',
    path: '../administration/logs',
    icon: <ReceiptLongIcon />
  },
  {
    key: 'admin.announcements',
    label: 'Announcements',
    path: '../administration/announcements',
    icon: <AnnouncementSharp />
  },
  {
    key: 'admin.rxEventMetrics',
    label: 'Rx Event Metrics',
    path: '../administration/rx-event-metrics',
    icon: <EventIcon />,
    visible: isSuperAdmin
  },
  {
    key: 'admin.athenaApis',
    label: 'Athena APIs',
    path: '../administration/mock-apis',
    icon: <RouteIcon />,
    visible: ctx => isSuperAdmin(ctx) && !ctx.isProdEnvironment
  }
];

const PROVIDERS_ADMIN: MenuItemDef[] = [
  {
    key: 'providers.directory',
    label: 'Directory',
    path: '../administration/providers/directory',
    icon: <ListAltIcon />
  },
  {
    key: 'providers.availability',
    label: 'Availability',
    path: '../administration/providers/availability',
    icon: <EventAvailableIcon />,
    visible: anyOf(isPsm, isSuperAdmin)
  }
];

const SYSTEM_ADMIN: MenuItemDef[] = [
  {
    key: 'system.notificationFiles',
    label: 'Notification Files',
    path: '../administration/system/notification-files',
    icon: <MonitorHeartIcon />,
    visible: not(isPsm)
  },
  {
    key: 'system.myCompany',
    label: 'My Pharmacy',
    path: ctx => `../administration/system/companies/${ctx.currentUser?.relyingParty.id}`,
    icon: <BusinessIcon />,
    visible: not(anyOf(isSuperAdmin, isPsm))
  },
  {
    key: 'system.allCompanies',
    label: 'All Pharmacies',
    path: '../administration/system/companies',
    icon: <BusinessIcon />,
    visible: anyOf(isSuperAdmin, isPsm)
  },
  {
    key: 'system.configurations',
    label: 'Configurations',
    path: '../administration/system/configurations',
    icon: <TuneIcon />,
    // Super Admin only; this showed Pharmacist Admin and ADT Admin a link to a page they cannot use.
    visible: isSuperAdmin
  },
  {
    key: 'system.dataImport',
    label: 'Data Import',
    path: '../administration/system/data-import',
    icon: <CloudUploadIcon />,
    visible: anyOf(isSuperAdmin, isPsm)
  },
  {
    key: 'system.eligibilityChecks',
    label: 'Eligibility Checks',
    path: '../administration/system/eligibility-checks',
    icon: <FactCheckIcon />,
    visible: anyOf(isSuperAdmin, isPsm)
  }
];

const QUICK_LINKS: MenuItemDef[] = [
  {
    key: 'quick.myPerformance',
    label: 'My Performance',
    path: '/administration/my-performance',
    icon: <QueryStatsIcon />,
    visible: ctx => isPharmacist(ctx) && mtm(ctx)
  },
  {
    key: 'quick.providers',
    label: 'Providers',
    path: '/administration/providers/directory',
    icon: <LocalHospitalIcon />,
    visible: ctx => isPharmacist(ctx) && rpm(ctx),
    ribbonPosition: 7
  },
  {
    key: 'quick.logs',
    label: 'Logs',
    path: '../administration/logs',
    icon: <ReceiptLongIcon />,
    visible: isPharmacist
  },
  {
    key: 'quick.announcements',
    label: 'Announcements',
    path: '../administration/announcements',
    icon: <AnnouncementSharp />,
    // The admin copy sits behind an admin-only gate, so this is the route for everyone else.
    visible: anyOf(isPharmacist, isProviderAdmin)
  }
];

/**
 * The navigation as it is rendered, top to bottom.
 *
 * A node either lists `items` or lists `children` — never both.  A node with a `title` renders as
 * a collapsible group; a node without one renders its items inline, with no header, which is how
 * the admin, timesheet and quick-link blocks have always appeared.
 *
 * Reordering the menu is moving lines in this array.  Renaming is editing the item arrays above.
 */
export const MENU_TREE: MenuNodeDef[] = [
  {
    key: MainMenuKeys.CareManagement,
    title: 'Care Management',
    visible: rpm,
    items: CARE_MANAGEMENT,
    openOn: ['/remote-patient-monitoring', '/care-management']
  },
  {
    key: MainMenuKeys.Provider,
    title: 'Provider',
    // Same RPM gate the items sat behind inside Care Management, so nobody gains or loses them.
    visible: rpm,
    items: PROVIDER_WORK,
    openOn: ['/remote-patient-monitoring', '/care-management']
  },
  {
    key: MainMenuKeys.Patients,
    title: 'Patients',
    // Site-map-only.  Starred Patients moved to Care Management and the rest were already
    // hidden, leaving nothing to head a section -- but the node still anchors their access.
    items: PATIENTS,
    openOn: ['/patients'],
    // Care Management owns those two areas, so /patients matching inside them means nothing here.
    openUnless: ['remote-patient-monitoring', 'care-management']
  },
  {
    key: MainMenuKeys.MedicationManagement,
    title: 'Medication Management',
    visible: mtm,
    items: MEDICATION_MANAGEMENT,
    openOn: ['/tasks', '/reports/my-dashboard']
  },
  {
    key: MainMenuKeys.TransitionalCareManagement,
    title: 'Transitional Care Management',
    visible: ctx => isAnyAdmin(ctx) && tcm(ctx),
    items: TRANSITIONAL_CARE_MANAGEMENT,
    openOn: ['/transitional-care-management']
  },
  {
    key: MainMenuKeys.Reports,
    title: 'Reports',
    icon: <AssessmentIcon />,
    // Pharmacists are included here but excluded from the store gate below, so Reports shows in
    // their sidebar while staying out of the app-grid.  Preserved as-is.
    //
    // Their service-line test is not decoration: Reports used to sit inside the Admin group,
    // whose gate was `isAnyAdmin || MTM || RPM || provider`, so a pharmacist at a company with
    // neither service line got no Reports at all.  Promoting Reports to the top level dropped
    // that outer gate, which would have handed them User Time and Metrics.  Admins and providers
    // satisfied both gates on their own, so only the pharmacist arm needs restating.
    visible: anyOf(isAnyAdmin, isProviderAdmin, ctx => isPharmacist(ctx) && anyOf(mtm, rpm)(ctx)),
    items: REPORTS,
    openOn: ['/administration/', '/reports/']
  },
  {
    title: 'Admin',
    // The whole section is off the nav -- every page under it is reachable from the site map,
    // and the rail's Admin button still opens the administration area.
    visible: anyOf(isAnyAdmin, mtm, rpm, isProviderAdmin),
    openOn: [
      '/reports/dashboard',
      '/reports/billing',
      '/reports/encounters',
      '/reports/claims',
      '/reports/tasks',
      '/reports/health-plan',
      '/reports/performance',
      '/reports/rpm-providers',
      '/reports/rpm-companies',
      '/reports/rpm-chart-reviews',
      '/reports/rpm-provider-signoffs',
      '/reports/session-time',
      '/reports/rpm-metrics',
      '/reports/ccm-reports',
      '/reports/payer-chart-requests',
      '/administration/dashboard',
      '/administration/users',
      '/administration/organizations',
      '/administration/health-plans',
      '/administration/providers',
      '/administration/my-performance',
      '/administration/timesheets',
      '/administration/invoices',
      '/administration/logs',
      '/administration/resources',
      '/administration/announcements',
      '/administration/mock-apis',
      '/administration/system/company',
      '/administration/system/companies',
      '/administration/system/notification-files'
    ],
    children: [
      {
        key: MainMenuKeys.UserAdmin,
        title: 'Users',
        icon: <UserGroup />,
        // Was `isAnyAdmin || (isPharmacist && those two lists are non-empty)`.  They are never
        // empty for a pharmacist, since Status is unconditional, so this is the same rule stated
        // plainly.  Timesheets ride along in this group, as they do today.
        visible: anyOf(isAnyAdmin, isPharmacist),
        items: [...USER_ADMIN, ...TIMESHEETS_AND_INVOICES],
        openOn: ['/administration']
      },
      {
        key: MainMenuKeys.Administration,
        visible: isAnyAdmin,
        items: ADMINISTRATION
      },
      {
        key: MainMenuKeys.ProvidersAdmin,
        title: 'All Providers',
        icon: <LocalHospitalIcon />,
        visible: isAnyAdmin,
        items: PROVIDERS_ADMIN,
        openOn: ['/administration/providers']
      },
      {
        key: MainMenuKeys.SystemAdmin,
        title: 'System',
        icon: <SettingsIcon />,
        visible: isAnyAdmin,
        items: SYSTEM_ADMIN,
        openOn: ['/system/notification-files', '/administration/system/companies', '/administration/system/configurations']
      },
      {
        // Non-admins get the timesheet items here instead of inside Users.  Pharmacists match
        // both placements and so see them twice — longstanding, and left alone by this refactor.
        visible: not(isAnyAdmin),
        items: TIMESHEETS_AND_INVOICES
      },
      {
        visible: always,
        items: QUICK_LINKS
      }
    ]
  }
];

/**
 * The flat, Map-shaped view of the menu that the app-grid (SecondaryMenu) reads.  Its gates are
 * the ones that surface has always used, which is not always what the sidebar uses — see the
 * comments on the four that differ.
 *
 * SecondaryMenu keeps its own allowlist of which sections reach the grid, so a new section here
 * needs adding there too or it renders in the sidebar and nowhere else.
 */
type StoreSectionDef = {
  key: MainMenuKeys;
  title: string;
  items: MenuItemDef[];
  visible: Gate;
};

const STORE_SECTIONS: StoreSectionDef[] = [
  { key: MainMenuKeys.Patients, title: 'Patients', items: PATIENTS, visible: always },
  { key: MainMenuKeys.MedicationManagement, title: 'Medication Management', items: MEDICATION_MANAGEMENT, visible: mtm },
  { key: MainMenuKeys.CareManagement, title: 'Care Management', items: CARE_MANAGEMENT, visible: rpm },
  { key: MainMenuKeys.Provider, title: 'Provider', items: PROVIDER_WORK, visible: rpm },
  {
    key: MainMenuKeys.TimesheetsInvoicesAdmin,
    title: 'Timesheets & Invoices',
    items: TIMESHEETS_AND_INVOICES,
    visible: anyOf(isAnyAdmin, mtm, rpm)
  },
  {
    key: MainMenuKeys.TransitionalCareManagement,
    title: 'Transitional Care Management',
    items: TRANSITIONAL_CARE_MANAGEMENT,
    // The sidebar shows TCM in production; the ribbon and app-grid do not.  Preserved verbatim —
    // drop the isProductionMode test to make the two agree.
    visible: ctx => !ctx.isProductionMode && isAnyAdmin(ctx) && tcm(ctx)
  },
  {
    key: MainMenuKeys.Reports,
    title: 'Reports',
    items: REPORTS,
    // Narrower than the sidebar gate, which also admits pharmacists.  Preserved verbatim.
    visible: anyOf(isAnyAdmin, isProviderAdmin)
  },
  {
    key: MainMenuKeys.UserAdmin,
    title: 'User Administration',
    items: USER_ADMIN,
    // Narrower than the sidebar gate, which also admits pharmacists.  Preserved verbatim.
    visible: isAnyAdmin
  },
  { key: MainMenuKeys.Administration, title: 'Administration', items: ADMINISTRATION, visible: isAnyAdmin },
  { key: MainMenuKeys.ProvidersAdmin, title: 'Providers', items: PROVIDERS_ADMIN, visible: isAnyAdmin },
  { key: MainMenuKeys.SystemAdmin, title: 'System Administration', items: SYSTEM_ADMIN, visible: isAnyAdmin },
  {
    key: MainMenuKeys.QuickLinksAdmin,
    title: 'Quick Links',
    items: QUICK_LINKS,
    // Wider in the sidebar, which renders quick links unconditionally.  Preserved verbatim.
    visible: anyOf(isAnyAdmin, mtm, rpm)
  }
];

const resolve = <T,>(value: T | ((ctx: MenuContext) => T), ctx: MenuContext): T =>
  typeof value === 'function' ? (value as (ctx: MenuContext) => T)(ctx) : value;

export type ResolveOptions = {
  /** Include entries the sidebar hides.  The site map wants them; the nav does not. */
  includeNavHidden?: boolean;
};

const resolveItems = (defs: MenuItemDef[] | undefined, ctx: MenuContext, options: ResolveOptions = {}): MainMenuItem[] =>
  (defs ?? [])
    .filter(def => options.includeNavHidden || !def.hiddenFromNav)
    .filter(def => (def.visible ?? always)(ctx))
    .map(def => ({
      key: def.key,
      navigatePath: resolve(def.path, ctx),
      icon: def.icon,
      label: resolve(def.label, ctx),
      hidden: false,
      ...(def.ribbonPosition !== undefined && { ribbonPosition: resolve(def.ribbonPosition, ctx) }),
      ...(def.buttonProps && { buttonProps: def.buttonProps })
    }));

/** A node with its gates applied and its items resolved to plain values. */
export type ResolvedMenuNode = {
  key?: MainMenuKeys;
  title?: string;
  icon?: ReactNode;
  openOn: string[];
  openUnless: string[];
  items: MainMenuItem[];
  children: ResolvedMenuNode[];
};

const resolveNode = (def: MenuNodeDef, ctx: MenuContext, options: ResolveOptions = {}): ResolvedMenuNode | null => {
  if (!(def.visible ?? always)(ctx)) return null;
  if (def.hiddenFromNav && !options.includeNavHidden) return null;

  const items = resolveItems(def.items, ctx, options);
  const children = (def.children ?? [])
    .map(child => resolveNode(child, ctx, options))
    .filter((node): node is ResolvedMenuNode => node !== null);

  // An empty group is not a group.  Replaces the ad-hoc length checks the render code made.
  if (items.length === 0 && children.length === 0) return null;

  return {
    key: def.key,
    title: def.title,
    icon: def.icon,
    openOn: def.openOn ?? [],
    openUnless: def.openUnless ?? [],
    items,
    children
  };
};

/** The sidebar for one user: nodes filtered and ordered, ready to render. */
export const resolveMenuTree = (ctx: MenuContext, options: ResolveOptions = {}): ResolvedMenuNode[] =>
  MENU_TREE.map(def => resolveNode(def, ctx, options)).filter((node): node is ResolvedMenuNode => node !== null);

/** The Map the ribbon and the app-grid read.  Recent Patients is filled in separately. */
export const resolveMenuSections = (ctx: MenuContext): Map<MainMenuKeys, MenuSectionType> => {
  const map = new Map<MainMenuKeys, MenuSectionType>();

  map.set(MainMenuKeys.RecentPatients, { title: 'Recent Patients', options: [] });

  STORE_SECTIONS.forEach(section => {
    map.set(section.key, {
      title: section.title,
      options: resolveItems(section.items, ctx, { includeNavHidden: true }),
      hidden: !section.visible(ctx)
    });
  });

  return map;
};

export const recentPatientItems = (recentPatients: RecentPatientType[], currentUser: LoggedUser | null): MainMenuItem[] =>
  recentPatients
    .map(patient => ({
      key: `recent.${patient.truentityId}`,
      navigatePath:
        getAccountRedirectUrl(
          patient.truentityId,
          MedicalServices.RPM,
          patient.rpmStatus,
          patient?.accountsMonitoringDevices ?? [],
          currentUser
        ) || '',
      icon: <PeopleIcon fontSize={'small'} />,
      label: patient.name,
      hidden: false
    }))
    .filter(item => item.navigatePath !== '');

/** Every item key this context can reach, for surfaces that need access rules without the tree. */
export const visibleItemKeys = (ctx: MenuContext): Set<string> => {
  const keys = new Set<string>();
  const visit = (nodes: ResolvedMenuNode[]) =>
    nodes.forEach(node => {
      node.items.forEach(item => item.key && keys.add(item.key));
      visit(node.children);
    });
  visit(resolveMenuTree(ctx, { includeNavHidden: true }));
  return keys;
};
